import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    GetDashboardResponse,
    LayoutBreakpoint,
    LayoutConfig,
    LayoutItem,
} from '@blockworks/platform/api/research/studio/dashboard';
import { type UseBooleanReturn } from '@blockworks/platform/hooks';
import { ObjUnion } from '@blockworks/platform/typescript';
import type { MenuItemProps } from '@blockworks/ui/menu';
import { createSafeContext } from '@blockworks/ui/utils';

import { VisualizationOptionsProvider, type VisualizationOptionsProviderProps } from '../../../context';
import { DashboardMode, type PreloadedDashboardData } from '../dashboard-grid.model';
import { type RemoveLayoutItem, removeLayoutItem, type UpdateLayoutItem, updateLayoutItem } from '../utils';
import { makePreloadedModeLayouts } from '../utils';

enum UniversalGritItemActionType {
    Remove = 'REMOVE_ITEM',
}

type GetGridItemActions = (
    item: LayoutItem,
    toolbox: {
        actionMenu: UseBooleanReturn;
        editMarkdownModal: UseBooleanReturn;
        removeLayoutItem: RemoveLayoutItem;
        updateLayoutItem: UpdateLayoutItem;
    } & Pick<DashboardGridContext, 'layouts' | 'setLayouts'>,
) => (MenuItemProps & { shouldRender: boolean; type?: UniversalGritItemActionType })[];

type UseDashboardGridStateProps = {
    layouts: LayoutConfig;
    /**
     * Controls whether the grid is editable or not
     * @default false
     */
    isEditable?: boolean;
    /**
     * Optional list of grid item (`Menu`) actions that will be displayed in each grid item header
     * as a popover menu when a grid `isEditable === true`
     **/
    getGridItemActions?: GetGridItemActions;
    componentSlots?: {
        VisualizationTitle?: React.FC<
            ObjUnion<{ queryId: string; title: string; visualizationId: string; slug?: string }>
        >;
    };
    visualizations?: GetDashboardResponse['data']['visualizations'];
    /**
     * Preloaded data for a public dashboard. This is only used
     * in `DashboardMode.Preloaded` mode and is optional.
     * If not used, the dashboard expects a proxy api endpoint
     * to fetch execution results for visualization items.
     */
    preloadedData?: PreloadedDashboardData;
    /**
     * Displays all visualizations in a dashboard. This is
     * only used in `DashboardMode.Preloaded` mode. When
     * `true` is passed, remember to pass execution results
     * to the `preloadedData` prop.
     *
     * @see `PreloadedDashboardData` for more information about the `preloadedData` prop shape.
     **/
    showAllItems?: boolean;
    /**
     * Controls the mode in which layouts are generated.
     * @default DashboardMode.Dynamic
     */
    mode?: DashboardMode;
};

const useDashboardGridState = ({
    layouts: layoutsProp = { base: [], lg: [] },
    isEditable: isEditableProp,
    getGridItemActions: getGridItemActionsProp,
    componentSlots,
    visualizations: visualizationsProp = [],
    mode = DashboardMode.Dynamic,
    preloadedData,
    showAllItems = false,
}: UseDashboardGridStateProps) => {
    const [layouts, setLayouts] = useState(layoutsProp);
    const [isEditable] = useState(isEditableProp);
    const [controlledBreakpoint, setControlledBreakpoint] = useState<LayoutBreakpoint>(LayoutBreakpoint.Large);
    const handleSetLayout = useCallback((layout: LayoutConfig) => setLayouts({ ...layout }), []);
    const getGridItemActions = useCallback(
        (item: LayoutItem, actionMenu: UseBooleanReturn, editMarkdownModal: UseBooleanReturn) =>
            getGridItemActionsProp?.(item, {
                actionMenu,
                editMarkdownModal,
                removeLayoutItem,
                updateLayoutItem,
                setLayouts,
                layouts,
            }),
        [getGridItemActionsProp, layouts],
    );

    const visualizations = useMemo(() => visualizationsProp, [visualizationsProp]);
    useEffect(() => {
        if (mode === DashboardMode.Preloaded && !showAllItems) {
            return handleSetLayout(makePreloadedModeLayouts(layoutsProp, visualizations));
        }
        setLayouts(layoutsProp);
    }, [layoutsProp, showAllItems, visualizations, mode, handleSetLayout]);

    return {
        layouts,
        isEditable,
        controlledBreakpoint,
        setControlledBreakpoint,
        setLayouts: handleSetLayout,
        getGridItemActions,
        componentSlots,
        visualizations,
        preloadedData,
        showAllItems,
        mode,
    };
};

type DashboardGridContext = ReturnType<typeof useDashboardGridState>;

const [DashboardGridContextProvider, useDashboardGridContext] = createSafeContext<DashboardGridContext>({
    strict: false,
    name: 'DashboardGridContext',
    hookName: 'useDashboardGridContext',
    providerName: 'DashboardGridContextProvider',
    defaultValue: {
        visualizations: [],
        isEditable: false,
        layouts: { lg: [], base: [] },
        controlledBreakpoint: LayoutBreakpoint.Large,
        setLayouts: () => {},
        setControlledBreakpoint: () => {},
        getGridItemActions: () => [],
        componentSlots: { VisualizationTitle: undefined },
        preloadedData: [],
        showAllItems: true,
        mode: DashboardMode.Dynamic,
    },
});

type DashboardGridProviderProps = React.PropsWithChildren<UseDashboardGridStateProps> &
    Pick<VisualizationOptionsProviderProps, 'features' | 'layout'>;

const DashboardGridProvider = ({
    layouts = { base: [], lg: [] },
    isEditable = false,
    children,
    componentSlots,
    getGridItemActions,
    features,
    layout,
    visualizations,
    mode = DashboardMode.Dynamic,
    preloadedData,
    showAllItems,
}: DashboardGridProviderProps) => {
    const dashboardGridState = useDashboardGridState({
        layouts,
        componentSlots,
        isEditable,
        getGridItemActions,
        visualizations,
        mode: mode as DashboardMode.Dynamic,
        preloadedData,
        showAllItems,
    });

    return (
        <DashboardGridContextProvider value={dashboardGridState}>
            <VisualizationOptionsProvider features={features} layout={layout}>
                {children}
            </VisualizationOptionsProvider>
        </DashboardGridContextProvider>
    );
};

export type { DashboardGridContext, DashboardGridProviderProps, GetGridItemActions };
export { DashboardGridProvider, DashboardMode, UniversalGritItemActionType, useDashboardGridContext };
